/**
 * Universal Toast notification system.
 *
 * Usage:
 *   import { toast } from '../ui/Toast.js';
 *   toast.success('Model added to catalog.');
 *   toast.error('Failed to save provider.');
 *   toast.info('Syncing models from OpenRouter...');
 *
 * The ToastProvider must be mounted once at the app root (see app.tsx).
 * The `toast` singleton is safe to call from anywhere — components, hooks,
 * utility functions, etc.
 *
 * @module apps/web/src/ui/Toast
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Loader2,
  X,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToastType = 'success' | 'error' | 'info' | 'loading';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  duration: number; // 0 = persistent (no auto-dismiss)
  createdAt: number;
}

interface ToastContextValue {
  push: (type: ToastType, message: string, duration?: number) => string;
  dismiss: (id: string) => void;
  update: (id: string, patch: Partial<Omit<ToastItem, 'id' | 'createdAt'>>) => void;
}

// ---------------------------------------------------------------------------
// Singleton (safe to import from anywhere)
// ---------------------------------------------------------------------------

let _push: ((type: ToastType, message: string, duration?: number) => string) | null = null;
let _dismiss: ((id: string) => void) | null = null;
let _update: ((id: string, patch: Partial<Omit<ToastItem, 'id' | 'createdAt'>>) => void) | null = null;

/**
 * Global toast API. Call `toast.success(...)`, `toast.error(...)`, etc. from
 * anywhere in the app. If the ToastProvider is not mounted, calls are no-ops.
 */
export const toast = {
  success: (message: string, duration = 4000) => _push?.('success', message, duration),
  error: (message: string, duration = 6000) => _push?.('error', message, duration),
  info: (message: string, duration = 4000) => _push?.('info', message, duration),
  loading: (message: string) => _push?.('loading', message, 0),
  dismiss: (id: string) => _dismiss?.(id),
  /**
   * Update an existing toast (e.g. replace a loading toast with success).
   * If the id is not found, this is a no-op.
   */
  update: (id: string, patch: Partial<Omit<ToastItem, 'id' | 'createdAt'>>) => _update?.(id, patch),
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ToastContext = createContext<ToastContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

let _idCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback((type: ToastType, message: string, duration = 4000): string => {
    const id = `toast-${++_idCounter}`;
    const item: ToastItem = { id, type, message, duration, createdAt: Date.now() };
    setItems((prev) => [...prev, item]);

    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  const update = useCallback((id: string, patch: Partial<Omit<ToastItem, 'id' | 'createdAt'>>) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

    // Handle duration change: if new duration > 0, set a timer; if 0, clear existing
    if (patch.duration !== undefined) {
      const existing = timersRef.current.get(id);
      if (existing) {
        clearTimeout(existing);
        timersRef.current.delete(id);
      }
      if (patch.duration > 0) {
        const timer = setTimeout(() => dismiss(id), patch.duration);
        timersRef.current.set(id, timer);
      }
    }
  }, [dismiss]);

  // Register the singleton callbacks
  useEffect(() => {
    _push = push;
    _dismiss = dismiss;
    _update = update;
    return () => {
      _push = null;
      _dismiss = null;
      _update = null;
    };
  }, [push, dismiss, update]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss, update }}>
      {children}
      {/* Toast viewport — fixed top-right, above everything */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Toast card
// ---------------------------------------------------------------------------

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const icon = (() => {
    switch (item.type) {
      case 'success': return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
      case 'error':   return <AlertCircle className="h-4 w-4 text-red-400" />;
      case 'loading': return <Loader2 className="h-4 w-4 text-sky-400 animate-spin" />;
      default:        return <Info className="h-4 w-4 text-sky-400" />;
    }
  })();

  return (
    <div
      className="pointer-events-auto flex items-start gap-2.5 rounded-xl border px-4 py-3 shadow-2xl backdrop-blur-md min-w-[300px] max-w-[420px] animate-[slideIn_0.2s_ease-out]"
      style={{
        backgroundColor: 'var(--color-bg-card)',
        borderColor: 'var(--color-border-default)',
        color: 'var(--body-color)',
      }}
    >
      <div className="mt-0.5 shrink-0">{icon}</div>
      <p className="flex-1 text-sm leading-snug" style={{ color: 'var(--body-color)' }}>{item.message}</p>
      {item.type !== 'loading' && (
        <button
          onClick={onDismiss}
          className="shrink-0 transition"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hook (for components that want the full API)
// ---------------------------------------------------------------------------

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Return no-op fallbacks so components don't crash if provider is missing
    return {
      push: () => '',
      dismiss: () => {},
      update: () => {},
    };
  }
  return ctx;
}
